import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';

/**
 * ASamplesX global audio player.
 *
 * One player serves the whole site. Every demo entry point anywhere in the theme
 * is just an element carrying `data-play`; this component claims them all through
 * a single delegated listener, so new surfaces need no wiring.
 *
 * Navigation model
 * ----------------
 * Horizon performs real multi-page navigation - it has no client-side router, and
 * `morph`/`section-renderer` only ever operate inside one document. A persistent
 * audio element across navigation is therefore impossible without introducing a
 * router, which is explicitly out of scope. Instead:
 *
 *   - The player markup lives outside `#MainContent` in `layout/theme.liquid` and
 *     carries a stable `view-transition-name`, so Horizon's native cross-document
 *     view transition carries the bar across the navigation without a flicker.
 *   - Full state (track, queue, index, position, playing, product relationship)
 *     is serialised to sessionStorage on every meaningful change and restored on
 *     load.
 *   - Resume is attempted on restore. Browsers that block autoplay leave the
 *     player accurately paused with a visible resume affordance, which is stated
 *     rather than hidden.
 *
 * Source layer
 * ------------
 * `resolveSource()` is the single seam between the player and wherever audio
 * actually lives. Today it passes through a direct URL. Swapping in SoundCloud (or
 * anything else) is a change to that one method - no other part of the player, and
 * no Liquid, needs to move.
 */

const STORAGE_KEY = 'asx:player';
const SAVE_THROTTLE_MS = 1000;

/** @typedef {{src: string, title: string, product: string, productUrl: string, artwork: string, duration: number, accent: string}} AsxTrack */

/**
 * Formats seconds as m:ss. Returns an em-dash placeholder when unknown, so the UI
 * never shows a fabricated duration.
 * @param {number} seconds
 * @returns {string}
 */
function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '--:--';
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/**
 * Builds a track from a `[data-play]` trigger element.
 * @param {HTMLElement} el
 * @returns {AsxTrack}
 */
function trackFromElement(el) {
  return {
    src: el.dataset.play || '',
    title: el.dataset.trackTitle || '',
    product: el.dataset.productTitle || '',
    productUrl: el.dataset.productUrl || '',
    artwork: el.dataset.artwork || '',
    duration: Number(el.dataset.duration) || 0,
    accent: el.dataset.accent || 'signal',
  };
}

class AsxPlayer extends Component {
  /** @type {AsxTrack[]} */
  #queue = [];
  /** @type {string} */
  #queueId = '';
  /** @type {number} */
  #index = -1;
  /** @type {number} */
  #lastSave = 0;
  /** @type {boolean} */
  #scrubbing = false;
  /** @type {boolean} */
  #restoring = false;
  #abortController = new AbortController();

  /** @returns {HTMLAudioElement | null} */
  get #audio() {
    const el = this.refs.audio;
    return el instanceof HTMLAudioElement ? el : null;
  }

  /** @returns {AsxTrack | null} */
  get #track() {
    return this.#queue[this.#index] ?? null;
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    const audio = this.#audio;

    // One delegated listener claims every `[data-play]` on the page, present or
    // future (rails morphed in by facets/section rendering included).
    document.addEventListener('click', this.#handleTriggerClick, { signal });

    // Horizon's single-media protocol: starting a video pauses us, and starting
    // us pauses every video. `detail.resource` distinguishes our own event.
    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      (event) => {
        if (/** @type {any} */ (event).detail?.resource === this) return;
        this.pause();
      },
      { signal }
    );

    if (audio) {
      audio.addEventListener('timeupdate', this.#handleTimeUpdate, { signal });
      audio.addEventListener('loadedmetadata', this.#handleLoadedMetadata, { signal });
      audio.addEventListener('play', () => this.#setState('playing'), { signal });
      audio.addEventListener('pause', this.#handlePause, { signal });
      audio.addEventListener('waiting', () => this.#setState('buffering'), { signal });
      audio.addEventListener('playing', () => this.#setState('playing'), { signal });
      audio.addEventListener('ended', this.#handleEnded, { signal });
      audio.addEventListener('error', this.#handleError, { signal });
    }

    window.addEventListener('pagehide', () => this.#save(true), { signal });
    document.addEventListener('keydown', this.#handleKeydown, { signal });

    this.#restore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#save(true);
    this.#abortController.abort();
  }

  /* ------------------------------------------------------------------ *
   * Source layer - the only place that knows where audio comes from.
   * ------------------------------------------------------------------ */

  /**
   * Resolves a track to a playable URL.
   *
   * Direct pass-through today. To add SoundCloud, resolve here (via a documented,
   * supported method - never a scraped stream URL) and return the playable URL, or
   * return an empty string to put the player into its explicit "no demo" state.
   *
   * @param {AsxTrack} track
   * @returns {Promise<string>}
   */
  async resolveSource(track) {
    return track.src;
  }

  /* ------------------------------------------------------------------ *
   * Trigger handling
   * ------------------------------------------------------------------ */

  /** @param {MouseEvent} event */
  #handleTriggerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-play]');
    if (!(trigger instanceof HTMLElement)) return;

    // A play control inside a card must never navigate, and must never bubble up
    // to the card's full-bleed product link.
    event.preventDefault();
    event.stopPropagation();

    const queueId = trigger.dataset.queue || 'default';
    const track = trackFromElement(trigger);

    // Same track tapped again: toggle rather than restart.
    if (this.#queueId === queueId && this.#track && this.#track.src === track.src && track.src) {
      this.toggle();
      return;
    }

    // The queue is snapshotted at the moment playback starts, in document order,
    // so prev/next keeps working after navigating to a page where the originating
    // rail no longer exists.
    const members = /** @type {HTMLElement[]} */ (
      Array.from(document.querySelectorAll(`[data-play][data-queue="${CSS.escape(queueId)}"]`))
    );

    this.#queue = (members.length ? members : [trigger]).map(trackFromElement);
    this.#queueId = queueId;
    this.#index = Math.max(
      0,
      this.#queue.findIndex((t) => t.src === track.src && t.title === track.title)
    );

    this.#load({ autoplay: true });
  };

  /* ------------------------------------------------------------------ *
   * Playback
   * ------------------------------------------------------------------ */

  /**
   * @param {object} [options]
   * @param {boolean} [options.autoplay]
   * @param {number} [options.position]
   */
  async #load({ autoplay = false, position = 0 } = {}) {
    const audio = this.#audio;
    const track = this.#track;
    if (!audio || !track) return;

    this.hidden = false;
    this.#render();

    if (!track.src) {
      // Stated, not silently ignored.
      this.#setState('nodemo');
      this.#announce(this.dataset.labelNoDemo || 'No audio demo available for this item');
      return;
    }

    this.#setState('loading');

    let src = '';
    try {
      src = await this.resolveSource(track);
    } catch {
      src = '';
    }

    if (!src) {
      this.#setState('error');
      return;
    }

    // `metadata` is the right default for a bar nobody has pressed yet, and the
    // wrong one the moment we know we are about to play: it fetches the headers,
    // then still has to buffer before a single sample can sound. Asking for the
    // audio itself up front is what shortens the silence after a navigation.
    if (autoplay && audio.preload !== 'auto') audio.preload = 'auto';

    if (audio.src !== src) audio.src = src;
    if (position > 0) {
      audio.currentTime = position;
    }

    if (autoplay) {
      await this.#play();
    } else {
      this.#setState('paused');
      this.#updateProgress();
    }

    this.#save(true);
  }

  async #play() {
    const audio = this.#audio;
    if (!audio || !audio.src) return;

    // Tell every other media element on the page to stand down.
    document.dispatchEvent(new MediaStartedPlayingEvent(this));

    try {
      await audio.play();
      this.removeAttribute('data-resume-blocked');
      this.#setState('playing');
    } catch {
      // Autoplay policy, almost always after a restore. Report accurately.
      this.#setState('paused');
      if (this.#restoring) this.setAttribute('data-resume-blocked', '');
    }
  }

  /** Toggles play/pause. Bound declaratively from the markup. */
  toggle = () => {
    const audio = this.#audio;
    if (!audio) return;
    this.#restoring = false;
    if (audio.paused) {
      this.#play();
    } else {
      this.pause();
    }
  };

  /** Pauses playback without tearing down state. */
  pause = () => {
    const audio = this.#audio;
    if (audio && !audio.paused) audio.pause();
  };

  /** Advances to the next track in the current queue. */
  next = () => {
    if (this.#index >= this.#queue.length - 1) return;
    this.#index += 1;
    this.#restoring = false;
    this.#load({ autoplay: true });
  };

  /** Returns to the previous track, or restarts if we are past 3 seconds. */
  previous = () => {
    const audio = this.#audio;
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0;
      return;
    }
    if (this.#index <= 0) return;
    this.#index -= 1;
    this.#restoring = false;
    this.#load({ autoplay: true });
  };

  /** Collapses the player to a slim strip. */
  toggleMinimized = () => {
    const next = !this.hasAttribute('data-minimized');
    this.toggleAttribute('data-minimized', next);
    this.refs.minimizeButton?.setAttribute('aria-expanded', String(!next));
    this.#measure();
    this.#save(true);
  };

  /** Mobile: expands the collapsed bar into the full transport. */
  toggleExpanded = () => {
    const next = !this.hasAttribute('data-expanded');
    this.toggleAttribute('data-expanded', next);
    this.refs.expandButton?.setAttribute('aria-expanded', String(next));
    this.#measure();
  };

  /** Stops playback, clears the session and dismisses the player. */
  close = () => {
    const audio = this.#audio;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    this.#queue = [];
    this.#queueId = '';
    this.#index = -1;
    this.hidden = true;
    this.removeAttribute('data-minimized');
    this.removeAttribute('data-expanded');
    this.removeAttribute('data-resume-blocked');
    this.#setState('idle');
    document.documentElement.style.setProperty('--asx-player-height', '0px');
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private mode */
    }
  };

  /* ------------------------------------------------------------------ *
   * Scrubbing and volume
   * ------------------------------------------------------------------ */

  /** @param {Event} event */
  handleSeekInput = (event) => {
    const audio = this.#audio;
    const input = event.target;
    if (!audio || !(input instanceof HTMLInputElement) || !Number.isFinite(audio.duration)) return;
    this.#scrubbing = true;
    const ratio = Number(input.value) / 1000;
    audio.currentTime = ratio * audio.duration;
    this.#updateProgress();
  };

  handleSeekCommit = () => {
    this.#scrubbing = false;
    this.#save(true);
  };

  /** @param {Event} event */
  handleVolumeInput = (event) => {
    const audio = this.#audio;
    const input = event.target;
    if (!audio || !(input instanceof HTMLInputElement)) return;
    audio.volume = Number(input.value) / 100;
    audio.muted = audio.volume === 0;
    this.#reflectMuted();
  };

  toggleMute = () => {
    const audio = this.#audio;
    if (!audio) return;
    audio.muted = !audio.muted;
    this.#reflectMuted();
  };

  #reflectMuted() {
    const audio = this.#audio;
    if (!audio) return;
    this.toggleAttribute('data-muted', audio.muted || audio.volume === 0);
    this.refs.muteButton?.setAttribute('aria-pressed', String(audio.muted));
  }

  /* ------------------------------------------------------------------ *
   * Audio element events
   * ------------------------------------------------------------------ */

  #handleTimeUpdate = () => {
    if (!this.#scrubbing) this.#updateProgress();
    const now = Date.now();
    if (now - this.#lastSave > SAVE_THROTTLE_MS) this.#save();
  };

  #handleLoadedMetadata = () => {
    this.#updateProgress();
  };

  #handlePause = () => {
    if (this.dataset.state !== 'ended') this.#setState('paused');
    this.#save(true);
  };

  #handleEnded = () => {
    if (this.#index < this.#queue.length - 1) {
      this.next();
      return;
    }
    this.#setState('ended');
    this.#save(true);
  };

  #handleError = () => {
    if (!this.#track?.src) return;
    this.#setState('error');
    this.#announce(this.dataset.labelError || 'This demo could not be played');
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.hidden || !this.contains(document.activeElement)) return;
    if (this.hasAttribute('data-expanded')) this.toggleExpanded();
    else if (!this.hasAttribute('data-minimized')) this.toggleMinimized();
  };

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  /** @param {string} state */
  #setState(state) {
    this.dataset.state = state;
    const playing = state === 'playing';
    const button = this.refs.playButton;
    if (button instanceof HTMLElement) {
      button.setAttribute('aria-pressed', String(playing));
      const label = playing
        ? this.dataset.labelPause || 'Pause'
        : this.dataset.labelPlay || 'Play';
      button.setAttribute('aria-label', label);
    }
    this.#reflectTriggerStates();
  }

  /** Mirrors playing state onto the originating `[data-play]` trigger, if present. */
  #reflectTriggerStates() {
    const track = this.#track;
    const playing = this.dataset.state === 'playing';
    for (const el of document.querySelectorAll('[data-play]')) {
      if (!(el instanceof HTMLElement)) continue;
      const active = Boolean(track) && el.dataset.play === track?.src && el.dataset.play !== '';
      el.toggleAttribute('data-playing', active && playing);
      el.setAttribute('aria-pressed', String(active && playing));
    }
  }

  #render() {
    const track = this.#track;
    if (!track) return;

    const { trackTitle, productTitle, artwork, viewProduct } = this.refs;

    if (trackTitle instanceof HTMLElement) trackTitle.textContent = track.title;
    if (productTitle instanceof HTMLElement) productTitle.textContent = track.product;

    if (artwork instanceof HTMLImageElement) {
      if (track.artwork) {
        artwork.src = track.artwork;
        artwork.alt = track.product ? `${track.product} artwork` : '';
        artwork.hidden = false;
      } else {
        artwork.removeAttribute('src');
        artwork.hidden = true;
      }
    }

    if (viewProduct instanceof HTMLAnchorElement) {
      if (track.productUrl) {
        viewProduct.href = track.productUrl;
        viewProduct.hidden = false;
      } else {
        viewProduct.hidden = true;
      }
    }

    this.dataset.accent = track.accent;
    this.toggleAttribute('data-has-queue', this.#queue.length > 1);

    this.#announce(`${track.title}${track.product ? ` - ${track.product}` : ''}`);
    this.#measure();
  }

  #updateProgress() {
    const audio = this.#audio;
    if (!audio) return;

    const { elapsed, duration, seek, waveFill } = this.refs;
    const hasDuration = Number.isFinite(audio.duration) && audio.duration > 0;
    const ratio = hasDuration ? audio.currentTime / audio.duration : 0;

    if (elapsed instanceof HTMLElement) elapsed.textContent = formatTime(audio.currentTime);
    if (duration instanceof HTMLElement) {
      duration.textContent = hasDuration ? formatTime(audio.duration) : '--:--';
    }
    if (waveFill instanceof HTMLElement) {
      waveFill.style.setProperty('--asx-progress', `${(ratio * 100).toFixed(2)}%`);
    }
    if (seek instanceof HTMLInputElement && !this.#scrubbing) {
      seek.value = String(Math.round(ratio * 1000));
      seek.setAttribute('aria-valuetext', `${formatTime(audio.currentTime)} of ${formatTime(audio.duration)}`);
    }
  }

  /** Publishes the player's height so page content can reserve space for it. */
  #measure() {
    requestAnimationFrame(() => {
      const height = this.hidden ? 0 : this.offsetHeight;
      document.documentElement.style.setProperty('--asx-player-height', `${height}px`);
    });
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.refs.status;
    if (status instanceof HTMLElement) status.textContent = message;
  }

  /* ------------------------------------------------------------------ *
   * Session persistence
   * ------------------------------------------------------------------ */

  /** @param {boolean} [force] */
  #save(force = false) {
    const audio = this.#audio;
    if (!audio || this.#index < 0) return;
    this.#lastSave = Date.now();

    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          queue: this.#queue,
          queueId: this.#queueId,
          index: this.#index,
          position: audio.currentTime || 0,
          playing: !audio.paused,
          minimized: this.hasAttribute('data-minimized'),
          volume: audio.volume,
          muted: audio.muted,
        })
      );
    } catch {
      // Storage can be unavailable (private mode, quota). The player still works
      // for the current page; it just will not survive navigation.
      void force;
    }
  }

  #restore() {
    /** @type {any} */
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      saved = null;
    }

    if (!saved || !Array.isArray(saved.queue) || !saved.queue.length) {
      this.hidden = true;
      return;
    }

    this.#queue = saved.queue;
    this.#queueId = saved.queueId || '';
    this.#index = Math.min(Math.max(0, saved.index || 0), saved.queue.length - 1);
    this.#restoring = true;

    if (saved.minimized) this.toggleAttribute('data-minimized', true);

    const audio = this.#audio;
    if (audio) {
      if (typeof saved.volume === 'number') audio.volume = saved.volume;
      audio.muted = Boolean(saved.muted);
      const volume = this.refs.volume;
      if (volume instanceof HTMLInputElement) volume.value = String(Math.round(audio.volume * 100));
      this.#reflectMuted();
    }

    this.#load({ autoplay: Boolean(saved.playing), position: Number(saved.position) || 0 });
  }
}

if (!customElements.get('asx-player-component')) {
  customElements.define('asx-player-component', AsxPlayer);
}

export default AsxPlayer;
